import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

export function useTripPlanDetailDrawerCopy(countryName?: string) {
  const intl = useIntl()

  const formatSeasonWeather = useCallback((minTemp: number, maxTemp: number, weather: string) => {
    return intl.formatMessage(
      {
        id: 'tPJwyH7YEk9ZRyrVnED-',
        defaultMessage: '{minTemp}°C – {maxTemp}°C Avg. {weather}',
        description: 'Temperature range and average weather for a season in best time to visit table',
      },
      { minTemp, maxTemp, weather },
    )
  }, [intl])

  const formatAreaName = useCallback((index: number, area: string) => {
    return intl.formatMessage(
      {
        id: 'o2LDDooOjsw6gA-UYSuf',
        defaultMessage: '{index}. {area}',
        description: 'Numbered area name in top areas list in the trip planner AI chat',
      },
      { index, area },
    )
  }, [intl])

  return useMemo(() => ({
    failedToLoadText: intl.formatMessage({
      id: 'FA17vx7NLkSZhFIBlGQD',
      defaultMessage: 'Failed to load additional information.',
      description: 'Error text when all trip plan detail sections fail to load',
    }),
    retryLabel: intl.formatMessage({
      id: 'U-ZcfakqpLsj2dlV-u4M',
      defaultMessage: 'Retry',
      description: 'Button label to retry loading trip plan detail sections',
    }),
    cityInCountryText: intl.formatMessage(
      {
        id: 'IrCYQGrjDZfcICrhlbRR',
        defaultMessage: 'City in {countryName}',
        description: 'Subtitle showing which country a city belongs to in trip plan detail',
      },
      { countryName: countryName ? formatPlacePhrase({ location: { name: countryName }, language: intl.locale, intent: 'location' }) : undefined },
    ),
    viewGalleryLabel: intl.formatMessage({
      id: 'VR9gBhLOmH6uKdz4K4GZ',
      defaultMessage: 'View gallery',
      description: 'Button label to open image gallery lightbox in trip plan detail',
    }),
    bestTimeToVisitHeading: intl.formatMessage({
      id: 'S798osdLcKcFQLKzPVwr',
      defaultMessage: 'Best Time to Visit',
      description: 'Heading for best time to visit section in trip plan detail',
    }),
    seasonHeader: intl.formatMessage({
      id: 'oZpAAoObVMOQuOdqY-Wl',
      defaultMessage: 'Season',
      description: 'Table header for season column in best time to visit section',
    }),
    weatherHeader: intl.formatMessage({
      id: 'JBSlAQSIQQaoKtdei1de',
      defaultMessage: 'Weather',
      description: 'Table header for weather column in best time to visit section',
    }),
    whyVisitHeader: intl.formatMessage({
      id: 'xnx6SqiQiV6CBZpeEg1R',
      defaultMessage: 'Why Visit',
      description: 'Table header for why visit column in best time to visit section',
    }),
    whatYoullLoveHeading: intl.formatMessage({
      id: 'neot_xLo154w2TBir6Y4',
      defaultMessage: 'What you\u2019ll love',
      description: 'Heading for what to love section in trip plan detail',
    }),
    topAreasHeading: intl.formatMessage({
      id: '5qveZGlLczJjuvPraVc7',
      defaultMessage: 'Top areas to visit',
      description: 'Heading for top areas to visit section in trip plan detail',
    }),
    insiderTipsHeading: intl.formatMessage({
      id: 'SmqFMCdvG9iJmtM4tF3s',
      defaultMessage: 'Insider Tips',
      description: 'Heading for insider tips section in trip plan detail',
    }),
    locationHeading: intl.formatMessage({
      id: 'YrfaTmB2ca_D95RIXBwK',
      defaultMessage: 'Location',
      description: 'Heading for location map section in trip plan detail',
    }),
    overviewTabLabel: intl.formatMessage({
      id: 'k3K93ejJsedzLiEqLJCM',
      defaultMessage: 'Overview',
      description: 'Tab label for the overview content in the trip planner place detail drawer',
    }),
    thingsToDoTabLabel: intl.formatMessage({
      id: 'ViFKXXGI9bD3YSMWEA-z',
      defaultMessage: 'Things to do',
      description: 'Tab label for the nearby experiences content in the trip planner place detail drawer',
    }),
    fromLabel: intl.formatMessage({
      id: 'dfcwDcH90Uz5YCe5kG37',
      defaultMessage: 'From',
      description: 'Price caption prefix when no night count is available on the map offer card',
    }),
    failedToLoadExperienceDetailText: intl.formatMessage({
      id: 'clAQJsTYp1RaYNVY1-Rz',
      defaultMessage: 'Failed to load experience details.',
      description: 'Error message shown when experience offer details fail to load in the drawer',
    }),
    showMapLabel: intl.formatMessage({
      id: '5HOM8ZapOXbEwqgm-QaI',
      defaultMessage: 'Show map',
      description: 'Button label shown in the experience drawer header to open the map for the current location or offer.',
    }),
    tryAgainLabel: intl.formatMessage({
      id: 'q2nIV5-Sco41UZtxNQNg',
      defaultMessage: 'Try again',
      description: 'Button label to retry loading hotel offer details after an error',
    }),
    formatSeasonWeather,
    formatAreaName,
  }), [intl, countryName, formatSeasonWeather, formatAreaName])
}
